/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createCommit,
  listCommits,
  getCommitDiff,
  deleteCommit,
  createComparison,
  getComparison,
  listComparisons,
  type CommitCreateRequest,
  type ComparisonCreateRequest,
} from '@/lib/api/commits'
import { toast } from 'sonner'

/**
 * Hook to fetch all commits for a sequence
 * OPTIMIZED: Better timeout handling, graceful degradation, and error recovery
 */
export function useCommits(sequenceId: string | undefined) {
  return useQuery({
    queryKey: ['commits', sequenceId],
    queryFn: async () => {
      if (!sequenceId) throw new Error('Sequence ID is required')

      try {
        // Call the API with a reasonable timeout
        const result = await listCommits(sequenceId)
        return result
      } catch (error: any) {
        // If it's a timeout or network error, return empty state instead of crashing
        if (error.name === 'AbortError' || error.message?.includes('fetch')) {
          console.warn('Commits fetch timed out or failed, returning empty state:', error)
          return {
            commits: [],
            total: 0,
            sequence_id: sequenceId,
          }
        }

        // For other errors, throw to trigger error state
        throw error
      }
    },
    enabled: !!sequenceId,
    // Retry with exponential backoff
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    // Shorter stale time since commits can change frequently
    staleTime: 1000 * 30, // 30 seconds
    // Keep in cache longer
    gcTime: 1000 * 60 * 30, // 30 minutes
  })
}

/**
 * Hook to create a new timeline commit
 */
export function useCreateCommit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CommitCreateRequest) => {
      return createCommit(data)
    },
    onSuccess: async (data) => {
      // Invalidate commits list for this sequence
      queryClient.invalidateQueries({ queryKey: ['commits', data.sequence_id] })

      // Refetch activity logs to show OTIO clip sync activities
      await queryClient.refetchQueries({ queryKey: ['activityLogs'] })

      // Refetch clips to show newly created clips from OTIO import
      await queryClient.refetchQueries({ queryKey: ['clips'] })

      // Invalidate ALL VFX shots queries (will match any project ID)
      // VFX shots are automatically detected and updated when commits are created
      queryClient.invalidateQueries({
        queryKey: ['vfx-shots']
      })
    },
  })
}

/**
 * Hook to get diff between two commits
 */
export function useCommitDiff(commitId: string | undefined, compareId: string | undefined) {
  return useQuery({
    queryKey: ['commit-diff', commitId, compareId],
    queryFn: async () => {
      if (!commitId || !compareId) {
        throw new Error('Both commit IDs are required')
      }
      return getCommitDiff(commitId, compareId)
    },
    enabled: !!commitId && !!compareId,
  })
}

/**
 * Hook to delete a timeline commit
 */
export function useDeleteCommit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      commitId,
    }: {
      commitId: string
      sequenceId: string
    }) => {
      return deleteCommit(commitId)
    },
    onSuccess: (_, { sequenceId }) => {
      toast.success('Commit deleted successfully')

      // Invalidate queries to refetch from server and ensure cache stays in sync
      // Note: We don't do optimistic updates here to avoid race conditions
      queryClient.invalidateQueries({ queryKey: ['commits', sequenceId] })
      queryClient.invalidateQueries({ queryKey: ['commit-diff'] })
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete commit')
    },
  })
}

// =============================================
// TIMELINE COMPARISON HOOKS (AIO-46)
// =============================================

/**
 * Hook to create an on-demand timeline comparison
 */
export function useCreateComparison() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      commitId,
      request,
    }: {
      commitId: string
      request: ComparisonCreateRequest
    }) => {
      return createComparison(commitId, request)
    },
    onSuccess: (data) => {
      // Check for VFX shot changes that require re-transcode
      const vfxChanges = data.vfx_changes || []
      const retranscodeRequired = (data.summary as any)?.retranscode_required || 0

      if (retranscodeRequired > 0 && vfxChanges.length > 0) {
        // Show warning for VFX shots that need re-transcode
        const shotNames = vfxChanges.map(change => change.shot_name).join(', ')
        toast.warning(
          `Comparison generated: ${retranscodeRequired} VFX shot(s) changed and need re-transcode`,
          {
            description: `Shots affected: ${shotNames}`,
            duration: 8000,
          }
        )
      } else {
        toast.success('Comparison generated successfully!')
      }

      // Invalidate comparisons list for this project
      queryClient.invalidateQueries({ queryKey: ['comparisons', data.project_id] })

      // Also invalidate the specific comparison query
      queryClient.invalidateQueries({ queryKey: ['comparison', data.id] })

      // Invalidate activity logs to show the comparison activity
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] })

      // Invalidate VFX shots to refresh any frame range updates
      if (vfxChanges.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['vfx-shots'] })
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to generate comparison')
    },
  })
}

/**
 * Hook to get a stored comparison by ID
 */
export function useComparison(comparisonId: string | undefined) {
  return useQuery({
    queryKey: ['comparison', comparisonId],
    queryFn: async () => {
      if (!comparisonId) throw new Error('Comparison ID is required')
      return getComparison(comparisonId)
    },
    enabled: !!comparisonId,
  })
}

/**
 * Hook to list all comparisons for a project
 */
export function useComparisons(projectId: string | undefined) {
  return useQuery({
    queryKey: ['comparisons', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')
      return listComparisons(projectId)
    },
    enabled: !!projectId,
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 10, // 10 minutes
  })
}
